import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, OverlayTrigger, Tooltip, Dropdown, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function HistoriqueCommandes() {
    const navigate = useNavigate();
    const [commandes, setCommandes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [stats, setStats] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date_commande', direction: 'desc' });
    const [viewMode, setViewMode] = useState('table');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchCommandes();
        fetchStats();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const fetchCommandes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setCommandes(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            envoyee: { color: '#3b82f6', icon: 'ri-send-plane-line', label: 'Envoyée' },
            recue_partielle: { color: '#f59e0b', icon: 'ri-stack-line', label: 'Partielle' },
            recue_complete: { color: '#10b981', icon: 'ri-check-double-line', label: 'Reçue' },
            annulee: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Annulée' }
        };
        const config = configs[statut] || configs.brouillon;
        
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredCommandes = commandes
        .filter(c => {
            const matchesSearch = 
                c.numero_commande?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.fournisseur_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase());
            
            if (dateDebut && new Date(c.date_commande) < new Date(dateDebut)) return false;
            if (dateFin && new Date(c.date_commande) > new Date(dateFin)) return false;
            
            return matchesSearch;
        })
        .sort((a, b) => {
            if (sortConfig.key === 'date_commande') {
                return sortConfig.direction === 'asc' 
                    ? new Date(a.date_commande) - new Date(b.date_commande)
                    : new Date(b.date_commande) - new Date(a.date_commande);
            }
            if (sortConfig.key === 'montant_ttc') {
                return sortConfig.direction === 'asc' 
                    ? (a.montant_ttc || 0) - (b.montant_ttc || 0)
                    : (b.montant_ttc || 0) - (a.montant_ttc || 0);
            }
            return 0;
        });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCommandes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCommandes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const totalCommandes = filteredCommandes.length;
    const totalMontant = filteredCommandes.reduce((sum, cmd) => sum + (cmd.montant_ttc || 0), 0);
    const commandesParStatut = filteredCommandes.reduce((acc, cmd) => {
        acc[cmd.statut] = (acc[cmd.statut] || 0) + 1;
        return acc;
    }, {});

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de l'historique...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchCommandes}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Alertes modernes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}

                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-history-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Historique des commandes</h4>
                                    <p className="text-muted small mb-0">
                                        {totalCommandes} commande(s) au total • {formatCurrency(totalMontant)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/achats/commandes')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/achats/commandes/nouveau')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-add-line"></i>
                                Nouvelle commande
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-shopping-cart-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total commandes</span>
                                    <h3 className="kpi-value">{totalCommandes}</h3>
                                    <span className="kpi-unit">tous statuts</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-check-double-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Reçues</span>
                                    <h3 className="kpi-value">{commandesParStatut.recue_complete || 0}</h3>
                                    <span className="kpi-unit">complètes</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-stack-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Partielles</span>
                                    <h3 className="kpi-value">{commandesParStatut.recue_partielle || 0}</h3>
                                    <span className="kpi-unit">en attente</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-send-plane-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Envoyées</span>
                                    <h3 className="kpi-value">{commandesParStatut.envoyee || 0}</h3>
                                    <span className="kpi-unit">en cours</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres avancés */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={4}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par numéro, fournisseur..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="date"
                                        value={dateDebut}
                                        onChange={(e) => setDateDebut(e.target.value)}
                                        className="rounded-pill"
                                        placeholder="Date début"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="date"
                                        value={dateFin}
                                        onChange={(e) => setDateFin(e.target.value)}
                                        className="rounded-pill"
                                        placeholder="Date fin"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => {
                                            setDateDebut('');
                                            setDateFin('');
                                            setFilterText('');
                                        }}
                                        className="w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
                                    >
                                        <i className="ri-refresh-line"></i>
                                        <span>Réinitialiser</span>
                                    </Button>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Form.Select 
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                        className="rounded-pill"
                                    >
                                        <option value="date_commande">Trier par date</option>
                                        <option value="montant_ttc">Trier par montant</option>
                                    </Form.Select>
                                </Col>
                                <Col md={6} className="d-flex justify-content-end gap-2">
                                    <Button 
                                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('table')}
                                        className="rounded-pill px-3"
                                        style={viewMode === 'table' ? { background: '#21ACCD', border: 'none' } : {}}
                                    >
                                        <i className="ri-table-line me-1"></i>Tableau
                                    </Button>
                                    <Button 
                                        variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('cards')}
                                        className="rounded-pill px-3"
                                        style={viewMode === 'cards' ? { background: '#21ACCD', border: 'none' } : {}}
                                    >
                                        <i className="ri-grid-line me-1"></i>Cartes
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <SortableHeader field="date_commande">Date</SortableHeader>
                                                <th>N° Commande</th>
                                                <th>Fournisseur</th>
                                                <SortableHeader field="montant_ttc">Montant</SortableHeader>
                                                <th>Statut</th>
                                                <th className="text-center pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.length > 0 ? (
                                                currentItems.map((cmd) => (
                                                    <tr key={cmd.id_commande_fournisseur} className="commande-row">
                                                        <td>
                                                            <small className="text-muted">
                                                                <i className="ri-calendar-line me-1 fs-10"></i>
                                                                {formatDate(cmd.date_commande)}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <Link to={`/achats/commandes/${cmd.id_commande_fournisseur}`} className="table-link">
                                                                {cmd.numero_commande}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="avatar-icon">
                                                                    <i className="ri-store-line"></i>
                                                                </div>
                                                                <div>
                                                                    <Link to={`/achats/fournisseurs/${cmd.id_fournisseur}`} className="table-link">
                                                                        {cmd.fournisseur_nom}
                                                                    </Link>
                                                                    <div><small className="text-muted">{cmd.code_fournisseur}</small></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="fw-bold text-primary">{formatCurrency(cmd.montant_ttc || 0)}</td>
                                                        <td>{getStatutBadge(cmd.statut)}</td>
                                                        <td className="text-center pe-4">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => navigate(`/achats/commandes/${cmd.id_commande_fournisseur}`)}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                       </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <i className="ri-history-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                            <h5 className="mt-3">Aucune commande dans l'historique</h5>
                                                            <p className="text-muted mb-3">
                                                                {filterText || dateDebut || dateFin 
                                                                    ? "Aucun résultat ne correspond à vos filtres"
                                                                    : "Commencez par créer votre première commande"}
                                                            </p>
                                                            {filterText || dateDebut || dateFin ? (
                                                                <Button 
                                                                    variant="outline-primary"
                                                                    onClick={() => {
                                                                        setFilterText('');
                                                                        setDateDebut('');
                                                                        setDateFin('');
                                                                    }}
                                                                    className="rounded-pill"
                                                                >
                                                                    <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                                </Button>
                                                            ) : (
                                                                <Button 
                                                                    variant="primary"
                                                                    onClick={() => navigate('/achats/commandes/nouveau')}
                                                                    className="rounded-pill"
                                                                    style={{ background: '#21ACCD', border: 'none' }}
                                                                >
                                                                    <i className="ri-add-line me-2"></i>Nouvelle commande
                                                                </Button>
                                                            )}
                                                        </div>
</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                        <div className="text-muted small">
                                            <i className="ri-file-list-line me-1"></i>
                                            Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredCommandes.length)} sur {filteredCommandes.length} commandes
                                        </div>
                                        <Pagination className="mb-0">
                                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                            {renderPagination()}
                                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                        </Pagination>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Cartes */}
                    {viewMode === 'cards' && (
                        <>
                            <Row className="g-4">
                                {currentItems.length > 0 ? (
                                    currentItems.map((cmd) => (
                                        <Col lg={6} xl={4} key={cmd.id_commande_fournisseur}>
                                            <Card className="border-0 shadow-sm rounded-3 h-100 commande-card">
                                                <Card.Body className="p-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div>
                                                            <Badge className="mb-2 px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                                                {cmd.numero_commande}
                                                            </Badge>
                                                            <h5 className="mb-1">{cmd.fournisseur_nom}</h5>
                                                            <small className="text-muted">{cmd.code_fournisseur}</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <h4 className="text-primary mb-0">{formatCurrency(cmd.montant_ttc || 0)}</h4>
                                                            <small className="text-muted">
                                                                <i className="ri-calendar-line me-1 fs-10"></i>
                                                                {formatDate(cmd.date_commande)}
                                                            </small>
                                                        </div>
                                                    </div>

                                                    <hr className="my-3" />

                                                    <div className="d-flex justify-content-between align-items-center">
                                                        {getStatutBadge(cmd.statut)}
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="action-btn"
                                                                onClick={() => navigate(`/achats/commandes/${cmd.id_commande_fournisseur}`)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))
                                ) : (
                                    <Col md={12}>
                                        <Card className="border-0 shadow-sm rounded-3 text-center py-5">
                                            <i className="ri-history-line fs-48 text-muted mb-3 d-block"></i>
                                            <h5 className="mt-3">Aucune commande dans l'historique</h5>
                                        </Card>
                                    </Col>
                                )}
                            </Row>

                            {/* Pagination pour la vue cartes */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredCommandes.length)} sur {filteredCommandes.length} commandes
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0ea5e9; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Avatar Icon */
                .avatar-icon {
                    width: 36px;
                    height: 36px;
                    background: #e2f3f5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .commande-row {
                    transition: all 0.2s ease;
                }
                
                .commande-row:hover {
                    background: #f8fafc;
                }
                
                /* Commande Card */
                .commande-card {
                    transition: all 0.3s ease;
                }
                
                .commande-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
            `}</style>
        </>
    );
}